/**
 * ObstacleLabelsOverlay - Renders name labels above obstacles in the building editor
 *
 * Shows a small label with obstacle name + gear button for each obstacle
 * in the currently-edited building. Labels follow camera movement via
 * map.on('move') subscription (same pattern as BuildingLabelsOverlay).
 */

import { useEffect, useState, useCallback } from 'react'
import { Settings } from 'lucide-react'
import { useObstacleStore } from '@/stores/obstacleStore'
import { project3DToScreen, type ScreenPosition } from '@/lib/utils/project-to-screen'
import { getObstacleScenePosition } from '@/lib/utils/obstacle-coordinates'
import type { Obstacle } from '@/types/obstacle'
import type { Building3DConfigExtended } from '@/services/3d/BuildingLoaderService'

interface ObstacleLabelsOverlayProps {
  map: mapboxgl.Map | null
  obstacles: Obstacle[]
  buildings: Building3DConfigExtended[]
  editingBuildingId: string | null
  mapCenter: { lat: number; lng: number } | null
}

interface ObstacleLabelPosition {
  obstacleId: string
  name: string
  x: number
  y: number
  visible: boolean
}

export function ObstacleLabelsOverlay({
  map,
  obstacles,
  buildings,
  editingBuildingId,
  mapCenter,
}: ObstacleLabelsOverlayProps) {
  const [labelPositions, setLabelPositions] = useState<ObstacleLabelPosition[]>([])
  const selectedObstacleId = useObstacleStore((s) => s.selectedObstacleId)
  const selectObstacle = useObstacleStore((s) => s.selectObstacle)

  // Filter obstacles: in edit mode → only editing building; in view mode → all
  const editingObstacles = useCallback(() => {
    if (editingBuildingId) {
      // Edit mode: only obstacles in the editing building
      const editingBuilding = buildings.find((b) => b.id === editingBuildingId)
      if (!editingBuilding?.generatedUnits) return []
      const unitIds = new Set(editingBuilding.generatedUnits.map((u) => u.id).filter(Boolean))
      return obstacles.filter((o) => unitIds.has(o.unitId))
    }
    // View mode (editingBuildingId === null): show all obstacles
    return obstacles
  }, [obstacles, buildings, editingBuildingId])

  const updatePositions = useCallback(() => {
    if (!map || !mapCenter) {
      setLabelPositions([])
      return
    }

    const filtered = editingObstacles()
    if (filtered.length === 0) {
      setLabelPositions([])
      return
    }

    const positions: ObstacleLabelPosition[] = []
    for (const obstacle of filtered) {
      const scenePos = getObstacleScenePosition(obstacle, buildings)
      if (!scenePos) continue

      const screenPos: ScreenPosition = project3DToScreen(
        [scenePos.x, 0, scenePos.z],
        map,
        mapCenter,
        -30 // smaller offset — obstacles are short
      )

      if (screenPos.visible) {
        positions.push({
          obstacleId: obstacle.id,
          name: obstacle.name,
          x: screenPos.x,
          y: screenPos.y,
          visible: true,
        })
      }
    }

    setLabelPositions(positions)
  }, [map, mapCenter, buildings, editingObstacles])

  // Subscribe to camera moves
  useEffect(() => {
    if (!map) return

    const handleMove = () => updatePositions()
    map.on('move', handleMove)

    // Initial update
    updatePositions()

    return () => {
      map.off('move', handleMove)
    }
  }, [map, updatePositions])

  // Update when obstacles change
  useEffect(() => {
    updatePositions()
  }, [updatePositions])

  if (labelPositions.length === 0) return null

  return (
    <>
      {labelPositions.map((pos) => {
        const isSelected = pos.obstacleId === selectedObstacleId
        return (
          <div
            key={pos.obstacleId}
            className="absolute z-20 pointer-events-auto"
            style={{
              left: pos.x,
              top: pos.y,
              transform: 'translate(-50%, -100%)',
            }}
          >
            <div
              className={`flex items-center gap-1 p-1 rounded-lg shadow-md backdrop-blur-sm ${
                isSelected
                  ? 'bg-primary/90 text-primary-foreground ring-2 ring-primary'
                  : 'bg-background/90'
              }`}
            >
              <span className="px-1.5 py-0.5 text-[10px] font-medium truncate max-w-[100px]">
                {pos.name}
              </span>
              <button
                onClick={(e) => {
                  e.stopPropagation()
                  selectObstacle(pos.obstacleId)
                }}
                className={`flex items-center justify-center w-4 h-4 rounded hover:bg-accent/50 ${
                  isSelected ? 'hover:bg-primary-foreground/20' : ''
                }`}
                title="Edit obstacle"
              >
                <Settings className="w-2.5 h-2.5" />
              </button>
            </div>
          </div>
        )
      })}
    </>
  )
}
